import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useParams } from "react-router-dom";

import CustomInput from "../../UI/PageInput/CustomInput";
import CustomDescriptionInput from "../../UI/PageInput/CustomDescriptionInput";
import CustomFileInput from "../../UI/PageInput/CustomFileInput";
import SelectedImagesBox from "./SelectedImagesBox";
import CenteredHeading from "../../UI/headingsUI/CenteredHeading";
import GrayButton from "../../UI/buttonUI/GrayButton";
import ExpenseViewDialog from "../../UI/viewDialogs/expense/ExpenseViewDialog";
import UseDialogBox from "../../../CustomHooks/UseDialogBox";
import ImageSlider from "../../UI/viewDialogs/ImageSlider";
import { getCompressImage } from "../../../functions/getCompressImage";
import PrimaryButton from "../../UI/buttonUI/PrimaryButton";
import { CirclePlus } from "lucide-react";
import { positiveNumberValidation } from "../../../validations/formValidations";

const SalaryAdjustmentForm = ({
  pageInfo,
  sendData,
  loading,
  successResponse,
}) => {
  const { showDialogBox, setShowDialogBox } = UseDialogBox();
  const [imageSliderPics, setImageSliderPics] = useState([]);
  const [imageSliderCurrentIndex, setImageSliderCurrentIndex] = useState();
  const [openViewDialog, setViewDialog] = useState(false);
  const { name, id, salary } = useParams();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      pictures: [], // Initialize pictures as an empty array
    },
  });

  const pic = watch("pictures");
  const files = watch("files");

  const { append, remove } = useFieldArray({
    control,
    name: "files",
  });

  useEffect(() => {
    getCompressImage(pic, append);
  }, [pic]);

  const onSubmit = (data) => {
    let adjustedAmount = parseFloat(data.amount);
    if (pageInfo.category === 2) {
      adjustedAmount = -Math.abs(adjustedAmount);
    }
    const modifyDesc =
      pageInfo.category === 1
        ? "Overtime - " + data.description
        : "Salary Deduction - " + data.description;
    const formData = {
      ...data,
      category_id: 15,
      amount: adjustedAmount,
      user_id: id,
      description: modifyDesc,
    };

    sendData(formData, reset);
  };

  const openImageSlider = () => {
    setViewDialog(false);
    setShowDialogBox(true);
    setImageSliderPics(successResponse.pictureList);
  };

  const closeImageSlider = () => {
    setViewDialog(true);
    setShowDialogBox(false);
  };

  // show success dialog on api response
  useEffect(() => {
    if (successResponse) {
      setViewDialog(true);
    }
  }, [successResponse]);

  return (
    <div className="form-container">
      <CenteredHeading title={pageInfo.name} />
      <div className="formDetails">
        <p>
          <strong>User Name</strong> : {name}
        </p>
        <p>
          <strong>User Id</strong> : {id}
        </p>
        <p>
          <strong>Salary</strong> : ₹{salary}
        </p>
      </div>
      <form
        className="form-border max-w-5xl mx-auto"
        onSubmit={!loading ? (e) => handleSubmit(onSubmit)(e) : undefined}
      >
        <div className="flex flex-wrap">
          <div className="leftCol">
            <CustomInput
              type="number"
              name="amount"
              labelText="Amount *"
              placeholder="Enter"
              register={{
                ...register("amount", {
                  ...positiveNumberValidation,
                  required: "Please Enter an amount",
                }),
              }}
              error={errors.amount}
            />
            <CustomDescriptionInput
              name="description"
              labelText="Description *"
              register={{
                ...register("description", {
                  required: "Please Enter Description",
                }),
              }}
              error={errors.description}
            />
          </div>
          <div className="rightCol">
            <>
              <CustomFileInput
                name="pictures"
                placeholder="Add Image (jpg, png)"
                accept=".png, .jpg, image/png, image/jpeg"
                register={{
                  ...register("pictures"),
                }}
                multiple={true}
              />
              <SelectedImagesBox
                selectedImages={files}
                setValue={setValue}
                name="pictures"
                remove={remove}
              />
            </>
          </div>
        </div>
        <div className="submitWrap">
          <p className="addWrap">
            <PrimaryButton
              btnText={
                !loading
                  ? `Add ${pageInfo.category === 1 ? "Overtime" : "Deduction"}`
                  : "Adding..."
              }
              loading={loading}
              Icon={CirclePlus}
              size={18}
            />
          </p>
          <p className="grayWrap">
            <GrayButton btnText="Clear" width="w-40" onClick={() => reset()} />
          </p>
        </div>
      </form>
      {openViewDialog && (
        <ExpenseViewDialog
          viewDetails={successResponse}
          successMsg={`Salary ${
            pageInfo.category === 1 ? "Bonus" : "Deduction"
          } Successfully Created!`}
          openImageSlider={openImageSlider}
          setImageSliderCurrentIndex={setImageSliderCurrentIndex}
        />
      )}
      {showDialogBox && (
        <ImageSlider
          title="Expense Photos"
          images={imageSliderPics}
          imageSliderCurrentIndex={imageSliderCurrentIndex}
          closeDialogBox={closeImageSlider}
        />
      )}
    </div>
  );
};
export default SalaryAdjustmentForm;
